import { useMemo, useState } from "react";
import { ComponentsHelper } from "../../../core/services/ComponentHelper";
import {
	ArrowDownload16Regular,
	ChevronDown16Regular,
	ChevronUp16Regular,
	Delete16Regular,
} from "@fluentui/react-icons";
import { IComponentDataItem } from "../../../core/types/IComponentDataItem";
import { IFileSystem } from "../../types/IFileSystem";
import { IDataIngestionTableRow } from "../../types/IDataIngestionTableRow";
import IconTextButton from "../../../repertoire/components/iconTextButton/IconTextButton";
import { IStoragePathItemData } from "../../types/IStoragePathItemData";
import { IRepertoireField } from "../../../repertoire/types/IRepertoireField";
import { IJobState } from "../../../redux/types/IJobState";

export interface IDataIngestionTableDivsProps {
	storagePathItemData: IComponentDataItem;
	containerDetailsWindowComponentData: IComponentDataItem;
	selectedFileSystem: IFileSystem;
	storagePaths: IStoragePathItemData[];
	onDownloadFile: (
		data: IStoragePathItemData,
		isRepertoireModule: boolean,
	) => void;
	isRepertoireModule: boolean;
	displayRemoveColumn?: boolean;
	removeAttachmentFromMaintenanceScreen: (
		fullName: string,
		guid: string,
		index: number,
	) => void;
	jobs: IJobState[];
}

const DataIngestionDivHeader = ({
	row,
	isOpen,
	onToggle,
	onDownloadFile,
}: {
	row: IDataIngestionTableRow;
	isOpen: boolean;
	onToggle: () => void;
	onDownloadFile: () => void;
}) => {
	const ToggleIcon = isOpen
		? ChevronUp16Regular
		: ChevronDown16Regular;

	return (
		<div className="dataDivHeader">
			<div className="dataDivInfoHeader">
				<div className="dataDivActions">
					<span title="Download file">
						<IconTextButton
							icon={ArrowDownload16Regular}
							onClick={() => onDownloadFile()}
						/>
					</span>
				</div>
				{row.FilenameTableHeader}
			</div>

			<div onClick={() => onToggle()}>
				<span style={{ pointerEvents: "none" }}>
					<IconTextButton icon={ToggleIcon} disabled={false} />
				</span>
			</div>
		</div>
	);
};

const DataIngestionDivContent = ({
	row,
	fields,
	displayRemoveColumn,
	onRemove
}: {
	row: IDataIngestionTableRow;
	fields: IRepertoireField[];
	displayRemoveColumn: boolean;
	onRemove: (record: IDataIngestionTableRow) => void;
}) => {
	function formatFileSize(bytes: number, decimals = 2) {
		if (bytes === 0) return "0 B";
		const k = 1024;
		const dm = decimals < 0 ? 0 : decimals;
		const sizes = ["B", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return (
			parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i]
		);
	}

	const formatField = (field: IDataIngestionTableRow, name: string) => {
		const data = field[name];
		switch (name) {
			case "fileSize":
				return typeof data === "number" ? formatFileSize(data) : "";
			default:
				return data;
		}
	};

	return (
		<div className="dataDivContent borderTop">
			{fields.map((field) => (
				<div className="dataDivContentRow" key={field.name}>
					<span className="dataDivLabel">{field.data}:</span>
					<span className="dataDivValue">
						{formatField(row, field.name)}
					</span>
				</div>
			))}
			{displayRemoveColumn && (
				<div className="dataDivContentRowIcons borderTop">
					<div className="dataDivIcon" onClick={() => onRemove(row)}>
						<IconTextButton
							icon={Delete16Regular}
							tooltip="Remove attachment"
						/>
						<span className="dataDivLabel">Delete</span>
					</div>
				</div>
			)}
		</div>
	);
};

export const DataIngestionTableDivs = (props: IDataIngestionTableDivsProps) => {
	const [openRows, setOpenRows] = useState<Record<string, boolean>>({});

	const {
		storagePathItemData,
		containerDetailsWindowComponentData,
		selectedFileSystem,
		displayRemoveColumn,
		removeAttachmentFromMaintenanceScreen,
	} = props;

	const dir: string = selectedFileSystem?.fullName;

	const orderedFields = useMemo(() => {
		const fields: IRepertoireField[] = [
			...storagePathItemData.fields.filter(
				(f) => f.componentInstance === dir,
			),
			...containerDetailsWindowComponentData.fields.filter((f) =>
				[
					"FilenameTableHeader",
					"LastModifiedTableHeader",
					"TrackingInformationTableHeader",
				].includes(f.name),
			),
		];

		let result = ComponentsHelper.orderFieldsBySpecificField(
			fields,
			"order",
		);

		if (!result || result.length === 0) {
			result = fields;
		}

		const nameFieldIndex = result.findIndex(
			(field) => field.name === "FilenameTableHeader",
		);

		if (nameFieldIndex > -1) {
			const field = result.splice(nameFieldIndex, 1)[0];
			result.unshift(field);
		}

		const fieldNamesToMove = [
			"LastModifiedTableHeader",
			"TrackingInformationTableHeader",
		];

		fieldNamesToMove.forEach((name) => {
			const fieldIndex = result.findIndex((field) => field.name === name);

			if (fieldIndex > -1) {
				const field = result.splice(fieldIndex, 1)[0];
				result.push(field);
			}
		});

		return result.filter((field) => field.name !== "FilenameTableHeader");
	}, [
		storagePathItemData.fields,
		containerDetailsWindowComponentData.fields,
		dir,
	]);

	const onDownloadFile = (row: IDataIngestionTableRow) => {
		const { onDownloadFile, isRepertoireModule, storagePaths } = props;
		const storageItem = storagePaths.find(
			(item) => item.name === row.FilenameTableHeader,
		);

		if (storageItem) {
			onDownloadFile(storageItem, isRepertoireModule);
		}
	};

	const data = useMemo(() => {
		const storagePaths = props.storagePaths;
		if (storagePaths.length !== 0) {
			for (var storageItem of storagePaths) {
				if (storageItem.metadata != null) {
					for (var job of props.jobs) {
						if (storageItem.metadata?.jobID == job.jobID) {
							storageItem.inProgress = true;
						}
					}
				}
			}
		}

		return storagePaths.map((row) => ({
			key: row?.fullName,
			FilenameTableHeader: row?.name,
			distributionCode: row?.metadata?.distributionCode,
			poolCode: row?.metadata?.poolCode,
			format: row?.metadata?.format,
			outputFormat: row?.metadata?.outputFormat,
			source: row?.metadata?.source,
			destination: row?.metadata?.destination,
			fileSize: row?.metadata?.fileSize,
			LastModifiedTableHeader: row?.lastModified,
			TrackingInformationTableHeader: row?.metadata?.trackingInformation,
			inProgress: row?.inProgress,
			isEditing: row?.isEditing,
			isDirectory: row?.isDirectory,
			isDirty: row?.isDirty,
			attachmentTypeCode: row?.attachmentTypeCode,
			attachmentTypeID: row?.attachmentTypeID,
			rightHolder: row?.metadata?.rightHolder,
			batchID: row?.metadata?.batchID,
			validationError: row?.validationError,
		})) as IDataIngestionTableRow[];
	}, [props.storagePaths, props.jobs]);

	const toggleRow = (key: string) => {
		setOpenRows((prev) => ({
			...prev,
			[key]: !prev[key],
		}));
	};

	const onRemoveFile = (record: IDataIngestionTableRow) => {
		const { isRepertoireModule } = props;
		if (!record.isDirectory && isRepertoireModule) {
			const splitName: string[] = (record.key ?? "").split("/");
			const index = data.findIndex((item) => item.key === record.key);
			removeAttachmentFromMaintenanceScreen(
				record.key,
				splitName[splitName.length - 1],
				index,
			);
		}
	};

	return (
		<div className="dataDivTable">
			{data.length > 0 ? (
				data.map((row) => {
					const isOpen = !!openRows[row.key];

					return (
						<div
							className={`dataDivRow ${!isOpen && "borderBottom"}`}
							key={row.key}
						>
							<DataIngestionDivHeader
								row={row}
								isOpen={isOpen}
								onToggle={() => toggleRow(row.key)}
								onDownloadFile={() => onDownloadFile(row)}
							/>

							{isOpen && (
								<DataIngestionDivContent
									row={row}
									fields={orderedFields}
									displayRemoveColumn={displayRemoveColumn}
									onRemove={onRemoveFile}
								/>
							)}
						</div>
					);
				})
			) : (
				<div className="noData">No records at the moment</div>
			)}
		</div>
	);
};
